import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { ProductivityHubPage } from '@internal/backstage-plugin-productivity-hub';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { UnifiedThemeProvider } from '@backstage/theme';
import {
  ScaffolderFieldExtensions,
  ScaffolderLayouts,
} from '@backstage/plugin-scaffolder-react';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
  configApiRef,
  oktaAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { TechUpdatesPage } from '@ncgt/plugin-tech-updates';
import { AwsMiniCostCenterPage } from '@ncgt/plugin-aws-mini-cost-center';
import { AwsS3BucketViewerPage } from '@ncgt/plugin-aws-s3-bucket-viewer';

import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { CustomCatalogPage } from './components/catalog/CatalogPage';
import { DelayingComponentFieldExtension } from './components/scaffolder/customScaffolderExtensions';
import { TwoColumnLayout } from './components/scaffolder/customScaffolderLayouts';
import {
  BackstageDarkTheme,
  BackstageLightTheme,
} from './theme/backstageTheme';
import { HomePage } from './components/home/HomePage';
import { techDocsPage } from './components/techdocs/TechDocsPage';
import { TemplateListPage } from './components/contribute/TemplateListPage';
import { FeaturedCatalogPage } from '@internal/backstage-plugin-featured-catalog';
import UserSettings from './components/analytics/UserSettings';
import { GithubStatisticsPage } from '@internal/backstage-plugin-github-statistics';
import { LeanixPage } from '@internal/backstage-plugin-leanix';
import { InnerSourceProgressPage } from './components/progress/InnerSourceProgressPage';
import { BadgesPage } from '@internal/backstage-plugin-badges';
import { AnalyticsDbPage } from '@internal/backstage-plugin-analytics-db';
import { CxonePage } from '@internal/backstage-plugin-cxone';
import { DeploymentDashboardPage } from '@internal/backstage-plugin-deployment-dashboard';

const app = createApp({
  apis,
  themes: [
    {
      id: 'backstage-light-theme',
      title: 'Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={BackstageLightTheme} children={children} />
      ),
    },
    {
      id: 'backstage-dark-theme',
      title: 'Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={BackstageDarkTheme} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      const providers: Array<SignInProviderConfig | 'guest'> = [];

      // We enable guest mode in develop
      if (configApi.getString('auth.environment') === 'development') {
        providers.push('guest');
      }

      // Push Okta if the config has values for it
      if (configApi.getOptionalConfig('auth.providers.okta')) {
        providers.push({
          id: 'okta-auth-provider',
          title: 'Okta',
          message: 'Sign in using Okta',
          apiRef: oktaAuthApiRef,
        });
      }

      return <SignInPage {...props} auto providers={providers} />;
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogPage />
    </Route>
    <Route path="/innersource/progress" element={<InnerSourceProgressPage />}>
      <InnerSourceProgressPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      {techDocsPage}
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          templateFilter={_entity => false}
          headerOptions={{
            pageTitleOverride: 'Contribute a New Component',
            title: 'Contribute a New Component',
          }}
          components={{
            EXPERIMENTAL_TemplateListPageComponent: TemplateListPage,
          }}
        />
      }
    />
    <Route
      path="/templates"
      element={
        <ScaffolderPage
          groups={[
            {
              title: 'Recommended',
              filter: entity =>
                entity?.metadata?.tags?.includes('recommended') ?? false,
            },
          ]}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <DelayingComponentFieldExtension />
      </ScaffolderFieldExtensions>
      <ScaffolderLayouts>
        <TwoColumnLayout />
      </ScaffolderLayouts>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage title="Tech Radar" width={1500} height={900} />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/aws-mini-cost-center" element={<AwsMiniCostCenterPage />} />
    <Route path="/aws-s3-bucket-viewer" element={<AwsS3BucketViewerPage />} />
    <Route path="/tech-updates" element={<TechUpdatesPage />} />
    <Route path="/featured-catalog" element={<FeaturedCatalogPage />} />
    <Route path="/github-statistics" element={<GithubStatisticsPage />} />
    <Route path="/leanix" element={<LeanixPage />} />
    <Route path="/badges" element={<BadgesPage />} />
    <Route path="/analytics-db" element={<AnalyticsDbPage />} />
    <Route path="/cxone" element={<CxonePage />} />
    <Route path="/metrics" element={<ProductivityHubPage />} />
    <Route path="/deployment-dashboard" element={<DeploymentDashboardPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <UserSettings />
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
